import React from 'react';
import { X, Mail, Phone, Users, Calendar, DoorClosed, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface BookingDetailsModalProps {
  booking: {
    property: { name: string };
    guest_first_name: string;
    guest_last_name: string;
    guest_count: number;
    check_in_date: string;
    check_out_date: string;
    cleaning_service: boolean;
    bed_sheets_service: boolean;
    guest_email?: string;
    guest_phone?: string;
    door_code?: string;
    comments?: string;
  };
  onClose: () => void;
}

export default function BookingDetailsModal({ booking, onClose }: BookingDetailsModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[100]">
      <div className="bg-white rounded-lg max-w-lg w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {booking.property.name}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <Users className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium">{booking.guest_first_name} {booking.guest_last_name}</p>
                <p className="text-sm text-gray-500">{booking.guest_count} personnes</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium">
                  {format(new Date(booking.check_in_date), 'PPP', { locale: fr })} - {format(new Date(booking.check_out_date), 'PPP', { locale: fr })}
                </p>
              </div>
            </div>

            {booking.guest_email && (
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-gray-400" />
                <a
                  href={`mailto:${booking.guest_email}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {booking.guest_email}
                </a>
              </div>
            )}

            {booking.guest_phone && (
              <div className="flex items-center gap-4">
                <Phone className="w-5 h-5 text-gray-400" />
                <a
                  href={`tel:${booking.guest_phone}`}
                  className="text-blue-600 hover:text-blue-800"
                >
                  {booking.guest_phone}
                </a>
              </div>
            )}

            {booking.door_code && (
              <div className="flex items-center gap-4">
                <DoorClosed className="w-5 h-5 text-gray-400" />
                <p className="font-mono bg-gray-100 px-2 py-1 rounded">
                  {booking.door_code}
                </p>
              </div>
            )}

            <div className="flex gap-2">
              {booking.cleaning_service && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  Ménage
                </span>
              )}
              {booking.bed_sheets_service && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Draps
                </span>
              )}
            </div>

            {booking.comments && (
              <div className="flex items-start gap-4">
                <MessageSquare className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                <div className="bg-gray-50 p-4 rounded-lg w-full">
                  <p className="text-gray-700 whitespace-pre-wrap">{booking.comments}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}